/**
 * Formats a `releaseNotes.ts` date (authored `DD-MM-YYYY`, e.g. "10-08-2026")
 * as "10 Aug 2026" — day, short month, full year, no leading zero.
 *
 * Deliberately does not use `new Date(value)`: `DD-MM-YYYY` is not ISO-8601,
 * so the native parser falls back to an engine-dependent heuristic that
 * reads a dash-separated, non-4-digit-leading string as `MM-DD-YYYY` —
 * silently swapping day and month for any day ≤ 12. Parsed here by regex
 * instead, the same reason `frontend/src/shared/ui/formatters.ts`'s
 * `formatCsDate` keeps its own short-month table rather than
 * `Intl.DateTimeFormat` (QFLY-484 — CLDR renamed en-GB "Sep" to "Sept"
 * between ICU versions).
 *
 * Total: never throws. Anything that isn't a valid `DD-MM-YYYY` string
 * (wrong shape, out-of-range day/month) is returned unchanged, so one
 * malformed hand-authored entry degrades gracefully instead of blanking
 * the whole release-notes render.
 */

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const DATE_RE = /^(\d{2})-(\d{2})-(\d{4})$/;

export function formatReleaseNoteDate(value: string): string {
  const match = DATE_RE.exec(value);
  if (!match) {
    return value;
  }

  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return value;
  }

  return `${day} ${SHORT_MONTHS[month - 1]} ${yyyy}`;
}
